export type RuntimeSessionState =
  | "provisioning"
  | "runtime_ready"
  | "running_input"
  | "idle"
  | "extending_reserve"
  | "stopping"
  | "stopped"
  | "expired"
  | "failed";

export type RuntimeSessionLike = {
  state?: RuntimeSessionState | string;
  billing_status?: string;
  billing_summary?: Record<string, unknown>;
};

export type RunSnapshotSessionMode = "one_shot" | "persistent" | string;

export function runSnapshotLaunchPayload(enabled: boolean, sessionMode: RunSnapshotSessionMode) {
  const persistent = String(sessionMode || "").trim().toLowerCase() === "persistent";
  return {
    save_run_context: enabled,
    snapshot_policy: enabled
      ? {
          mode: "outputs_and_logs",
          retention_days: 30,
          capture: ["prompt", "outputs", "logs", "input_refs"],
          runtime_scope: persistent ? "session_turns" : "job",
        }
      : {
          mode: "none",
          retention_days: 0,
          capture: [],
          runtime_scope: persistent ? "session_turns" : "job",
        },
    context_recall: enabled
      ? {
          enabled: true,
          method_preference: ["mount", "object_download", "materialise"],
        }
      : {
          enabled: false,
          method_preference: [],
        },
    storage_context: enabled
      ? {
          enabled: true,
          output_target: "workspace_default",
          stage_outputs: true,
        }
      : {
          enabled: false,
          output_target: "none",
          stage_outputs: false,
        },
  };
}

export function runSnapshotSummaryLabel(enabled: boolean) {
  return enabled ? "Snapshots on" : "No snapshots";
}

export function runtimeSessionIsActive(session: RuntimeSessionLike) {
  return !["stopped", "expired", "failed"].includes(String(session.state || "").toLowerCase());
}

export function runtimeSessionStateLabel(session: RuntimeSessionLike) {
  switch (String(session.state || "").toLowerCase()) {
    case "running_input":
      return "Running input";
    case "runtime_ready":
      return "Runtime ready";
    case "idle":
      return "Idle";
    case "extending_reserve":
      return "Extending reserve";
    case "stopping":
      return "Stopping";
    case "stopped":
      return "Stopped";
    case "expired":
      return "Expired";
    case "failed":
      return "Failed";
    default:
      return "Provisioning";
  }
}

export function runtimeSessionFinalCostMicros(session: RuntimeSessionLike) {
  const summary = session.billing_summary || {};
  const raw =
    summary.wallet_captured_amount_micros ||
    summary.wallet_actual_amount_micros ||
    summary.wallet_debit_amount_micros ||
    0;
  const parsed = Number(raw);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : 0;
}
